import { useMutation } from '@apollo/client';
import { useEffect, useState, type ReactNode } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useSnapshot } from 'valtio';
import { DateTime } from 'luxon';
import { authStore } from '../../../store/authStore';
import { modalActions } from '../../../store/authStore/modalStore';
import { Button } from '../../components/button';
import { Modal } from '../../components/modal';
import RangeCalendar from '../../components/RangeCalendar';
import { Muted, Stack, Strong } from '../../components/ui';
import { ADD_FAVORITE, REMOVE_FAVORITE } from '../../services/carService/queries';
import type { IStayCardFieldsFragment } from '../../services/stayService/__generated__/queries.generated';
import { toPublicS3ObjectUrl } from '../../utils/s3PublicUrl';
import { PROPERTY_TYPE_LABELS } from '../StayHostPage/stayForm';
import { dateRangeLabel, type StayGuests } from './staySearchState';

// ─── card ──────────────────────────────────────────────────────────────────

const CardRoot = styled.article`
  ${tw`relative flex flex-col cursor-pointer`}
  gap: 2px;
  min-width: 0;
  &:hover .stay-card-arrow {
    opacity: 1;
  }
`;

const Photo = styled.div`
  ${tw`relative w-full overflow-hidden`}
  aspect-ratio: 20 / 19;
  border-radius: var(--radius-card, 12px);
  background: var(--color-bg-secondary);
  margin-bottom: 10px;
`;

const Slides = styled.div<{ $index: number }>`
  display: flex;
  height: 100%;
  transform: translateX(${({ $index }) => -100 * $index}%);
  transition: transform 0.3s ease;
  img {
    flex: 0 0 100%;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const SlideArrow = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${({ $side }) => ($side === 'left' ? 'left: 12px;' : 'right: 12px;')}
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.92);
  color: #222;
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.04),
    0 2px 4px rgba(0, 0, 0, 0.18);
  cursor: pointer;
  opacity: 0;
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
  &:hover {
    transform: translateY(-50%) scale(1.04);
    background: #fff;
  }
  @media (hover: none) {
    display: none;
  }
`;

const Dots = styled.div`
  position: absolute;
  left: 50%;
  bottom: 10px;
  transform: translateX(-50%);
  display: flex;
  gap: 5px;
  span {
    width: 6px;
    height: 6px;
    border-radius: 9999px;
    background: rgba(255, 255, 255, 0.6);
  }
  span[data-on='true'] {
    background: #fff;
  }
`;

const HeartButton = styled.button<{ $on: boolean }>`
  ${tw`absolute flex items-center justify-center w-8 h-8 p-0 cursor-pointer`}
  top: 12px;
  right: 12px;
  border: none;
  background: transparent;
  transition: transform 0.1s ease;
  &:active {
    transform: scale(0.92);
  }
  svg {
    width: 24px;
    height: 24px;
    stroke: #fff;
    stroke-width: 2;
    fill: ${({ $on }) => ($on ? '#ff385c' : 'rgba(0, 0, 0, 0.5)')};
  }
`;

const Badge = styled.span`
  ${tw`absolute text-sm font-semibold`}
  top: 12px;
  left: 12px;
  padding: 5px 10px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.95);
  color: #222;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
`;

const Line = styled.div`
  ${tw`flex items-start justify-between gap-2`}
  font-size: 15px;
  line-height: 19px;
  color: var(--color-text-primary);
  > :first-child {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  > :last-child {
    flex-shrink: 0;
  }
`;

const Sub = styled.div`
  font-size: 15px;
  line-height: 19px;
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Price = styled.div`
  margin-top: 4px;
  font-size: 15px;
  line-height: 19px;
  color: var(--color-text-primary);
  strong {
    font-weight: 600;
  }
`;

/** "Cabin in Boulder, CO" from the masked location. */
export function stayCardTitle(
  listing: Pick<IStayCardFieldsFragment, 'stayDetails' | 'location'>,
): string {
  const type = listing.stayDetails?.propertyType
    ? PROPERTY_TYPE_LABELS[listing.stayDetails.propertyType]
    : 'Place';
  const where = [listing.location?.city, listing.location?.state].filter(Boolean).join(', ');
  return where ? `${type} in ${where}` : type;
}

/** "2 bedrooms · 3 beds" */
export function stayCardRooms(stay?: IStayCardFieldsFragment['stayDetails']): string {
  if (!stay) return '';
  const bedrooms =
    stay.bedrooms === 0 ? 'Studio' : `${stay.bedrooms} bedroom${stay.bedrooms === 1 ? '' : 's'}`;
  return `${bedrooms} · ${stay.beds} bed${stay.beds === 1 ? '' : 's'}`;
}

/** Adds or removes the place from the guest's wishlist; asks to sign in first. */
export function useWishlistToggle(listingId: string, initial?: boolean | null) {
  const { isAuthenticated } = useSnapshot(authStore);
  const [saved, setSaved] = useState(Boolean(initial));
  const [add] = useMutation(ADD_FAVORITE);
  const [remove] = useMutation(REMOVE_FAVORITE);
  useEffect(() => setSaved(Boolean(initial)), [initial]);

  const toggle = async () => {
    if (!isAuthenticated) {
      modalActions.openSignInModal();
      return;
    }
    const next = !saved;
    setSaved(next);
    try {
      if (next) await add({ variables: { input: { carId: listingId } } });
      else await remove({ variables: { input: { carId: listingId } } });
    } catch {
      setSaved(!next);
      toast.error('Could not update your wishlist');
    }
  };
  return { saved, toggle };
}

export function HeartIcon() {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true">
      <path d="M16 28c7-4.73 14-10 14-17a6.98 6.98 0 0 0-7-7c-1.8 0-3.58.68-4.95 2.05L16 8.1l-2.05-2.05a6.98 6.98 0 0 0-9.9 0A6.98 6.98 0 0 0 2 11c0 7 7 12.27 14 17z" />
    </svg>
  );
}

function SlideChevron({ left }: { left?: boolean }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width={12}
      height={12}
      fill="none"
      stroke="currentColor"
      strokeWidth={2.6}
      aria-hidden="true"
    >
      <path d={left ? 'M10 2 4 8l6 6' : 'M6 2l6 6-6 6'} />
    </svg>
  );
}

/** "Oct 19 to 22" for a card when the search has dates. */
function cardDates(dates?: { checkIn?: string; checkOut?: string }): string | null {
  if (!dates?.checkIn || !dates.checkOut) return null;
  return dateRangeLabel(dates.checkIn, dates.checkOut);
}

/**
 * A place in a list, Airbnb's card: photo carousel with dots, "Guest favorite",
 * the heart, then type and town with the rating, the title, rooms, and price.
 */
export function StayCard({
  listing,
  quote,
  onOpen,
  onHover,
  dates,
}: {
  listing: IStayCardFieldsFragment;
  quote?: { nights: number; totalPrice: number } | null;
  onOpen: () => void;
  onHover?: (hovering: boolean) => void;
  dates?: { checkIn?: string; checkOut?: string };
}) {
  const { saved, toggle } = useWishlistToggle(listing.id, listing.isFavorite);
  const photos = (listing.images ?? [])
    .slice(0, 5)
    .map(image => toPublicS3ObjectUrl(image.imageUrl));
  const [index, setIndex] = useState(0);
  const rating = Number(listing.averageRating) || 0;
  const reviews = listing.totalRatings ?? 0;
  const favorite = rating >= 4.8 && reviews >= 3;
  const step = (event: React.MouseEvent, by: number) => {
    event.stopPropagation();
    setIndex(current => Math.min(photos.length - 1, Math.max(0, current + by)));
  };
  const range = cardDates(dates);
  return (
    <CardRoot
      data-testid={`stay-card-${listing.id}`}
      onClick={onOpen}
      onMouseEnter={() => onHover?.(true)}
      onMouseLeave={() => onHover?.(false)}
    >
      <Photo>
        <Slides $index={index}>
          {photos.map((src, n) => (
            <img
              key={src + n}
              src={src}
              alt={n === 0 ? (listing.name ?? '') : ''}
              loading={n === 0 ? 'eager' : 'lazy'}
            />
          ))}
        </Slides>
        {favorite && <Badge>Guest favorite</Badge>}
        <HeartButton
          type="button"
          $on={saved}
          aria-label={saved ? 'Remove from wishlist' : 'Save to wishlist'}
          aria-pressed={saved}
          onClick={event => {
            event.stopPropagation();
            toggle();
          }}
        >
          <HeartIcon />
        </HeartButton>
        {index > 0 && (
          <SlideArrow
            type="button"
            className="stay-card-arrow"
            $side="left"
            aria-label="Previous photo"
            onClick={event => step(event, -1)}
          >
            <SlideChevron left />
          </SlideArrow>
        )}
        {index < photos.length - 1 && (
          <SlideArrow
            type="button"
            className="stay-card-arrow"
            $side="right"
            aria-label="Next photo"
            onClick={event => step(event, 1)}
          >
            <SlideChevron />
          </SlideArrow>
        )}
        {photos.length > 1 && (
          <Dots aria-hidden="true">
            {photos.map((src, n) => (
              <span key={src + n} data-on={n === index} />
            ))}
          </Dots>
        )}
      </Photo>
      <Line>
        <Strong>{stayCardTitle(listing)}</Strong>
        {rating > 0 ? (
          <span>
            ★ {rating.toFixed(2)}
            {reviews ? ` (${reviews})` : ''}
          </span>
        ) : (
          <span>★ New</span>
        )}
      </Line>
      <Sub>{listing.name}</Sub>
      <Sub>{range ?? stayCardRooms(listing.stayDetails)}</Sub>
      <Price>
        {quote ? (
          <span>
            <strong style={{ textDecoration: 'underline' }}>
              ${Math.round(quote.totalPrice).toLocaleString()}
            </strong>{' '}
            for {quote.nights} night{quote.nights === 1 ? '' : 's'}
          </span>
        ) : (
          <span>
            <strong>${Math.round(Number(listing.weekdayPrice) || 0).toLocaleString()}</strong> night
          </span>
        )}
      </Price>
    </CardRoot>
  );
}

/** Airbnb's column steps: 1, 2, 3, 4, 5, then 6 on very wide screens. */
export const StayGrid = styled.div`
  display: grid;
  column-gap: 24px;
  row-gap: 40px;
  grid-template-columns: 1fr;
  @media (min-width: 550px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (min-width: 950px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (min-width: 1128px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  @media (min-width: 1440px) {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
  @media (min-width: 1880px) {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
`;

// ─── guests ────────────────────────────────────────────────────────────────

const Row = styled.div`
  ${tw`flex items-center justify-between gap-4 py-4`}
  border-bottom: 1px solid var(--color-border);
  &:last-child {
    border-bottom: none;
  }
`;

const Round = styled.button`
  ${tw`flex items-center justify-center w-8 h-8 text-lg leading-none cursor-pointer`}
  border-radius: 9999px;
  border: 1px solid var(--color-border-strong);
  background: var(--color-bg-elevated, #fff);
  color: var(--color-text-primary);
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const GUEST_ROWS: {
  key: keyof StayGuests;
  label: string;
  hint: string;
  min: number;
  max: number;
}[] = [
  { key: 'adults', label: 'Adults', hint: 'Ages 13 or above', min: 1, max: 16 },
  { key: 'children', label: 'Children', hint: 'Ages 2 to 12', min: 0, max: 15 },
  { key: 'infants', label: 'Infants', hint: 'Under 2', min: 0, max: 5 },
  {
    key: 'pets',
    label: 'Pets',
    hint: 'Bringing a service animal? They do not count',
    min: 0,
    max: 5,
  },
];

/** Who is coming. `maxGuests` caps adults + children; pets are off when the place takes none. */
export function GuestsPicker({
  value,
  onChange,
  maxGuests,
  petsAllowed = true,
}: {
  value: StayGuests;
  onChange: (next: StayGuests) => void;
  maxGuests?: number;
  petsAllowed?: boolean;
}) {
  const people = value.adults + value.children;
  return (
    <div data-testid="stay-guests-picker">
      {GUEST_ROWS.filter(row => row.key !== 'pets' || petsAllowed).map(row => {
        const current = value[row.key];
        const atPeopleMax =
          maxGuests !== undefined &&
          (row.key === 'adults' || row.key === 'children') &&
          people >= maxGuests;
        return (
          <Row key={row.key}>
            <div>
              <Strong>{row.label}</Strong>
              <div>
                <Muted $size="sm">{row.hint}</Muted>
              </div>
            </div>
            <Stack $gap={4} style={{ flexDirection: 'row', alignItems: 'center' }}>
              <Round
                type="button"
                aria-label={`Fewer ${row.label.toLowerCase()}`}
                disabled={current <= row.min}
                onClick={() => onChange({ ...value, [row.key]: current - 1 })}
              >
                −
              </Round>
              <span data-testid={`guests-${row.key}`} style={{ width: 20, textAlign: 'center' }}>
                {current}
              </span>
              <Round
                type="button"
                aria-label={`More ${row.label.toLowerCase()}`}
                disabled={current >= row.max || atPeopleMax}
                onClick={() => onChange({ ...value, [row.key]: current + 1 })}
              >
                +
              </Round>
            </Stack>
          </Row>
        );
      })}
      {maxGuests !== undefined && (
        <Muted $size="sm">
          This place has a maximum of {maxGuests} guests, not including infants.
        </Muted>
      )}
    </div>
  );
}

// ─── dates ─────────────────────────────────────────────────────────────────

/** A two-month-ahead night picker in a modal. */
export function DatesModal({
  open,
  onClose,
  checkIn,
  checkOut,
  onApply,
  blocked,
  minNights,
  maxNights,
  timezone,
  lockedStart,
}: {
  open: boolean;
  onClose: () => void;
  checkIn?: string;
  checkOut?: string;
  onApply: (checkIn?: string, checkOut?: string) => void;
  blocked?: Set<string>;
  minNights?: number | null;
  maxNights?: number | null;
  timezone?: string;
  /** A stay in progress: check-in stays, only checkout moves. */
  lockedStart?: boolean;
}) {
  const today = DateTime.now()
    .setZone(timezone || 'local')
    .toISODate() as string;
  const [value, setValue] = useState({ start: checkIn ?? null, end: checkOut ?? null });
  useEffect(() => {
    if (open) setValue({ start: checkIn ?? null, end: checkOut ?? null });
  }, [open, checkIn, checkOut]);
  return (
    <Modal isOpen={open} onClose={onClose} title="Select dates" modalWidth="440px">
      <Stack $gap={4}>
        <Strong>
          {value.start && value.end ? dateRangeLabel(value.start, value.end) : 'Select dates'}
        </Strong>
        <Muted $size="sm">
          {value.start && !value.end ? 'Now pick your checkout date' : 'Pick your check-in date'}
          {minNights && minNights > 1 ? `. Minimum stay: ${minNights} nights` : ''}
        </Muted>
        <RangeCalendar
          mode="nights"
          value={value}
          onChange={setValue}
          minKey={today}
          maxKey={DateTime.fromISO(today).plus({ months: 18 }).toISODate() as string}
          todayKey={today}
          blocked={blocked}
          minNights={minNights}
          maxNights={maxNights}
          lockedStart={lockedStart}
        />
        <Stack $gap={4} style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <Button
            type="button"
            theme="text"
            text="Clear dates"
            onClick={() => setValue({ start: null, end: null })}
          />
          <Button
            type="button"
            text="Apply"
            isDisabled={Boolean(value.start) !== Boolean(value.end)}
            dataTestId="stay-dates-apply"
            onClick={() => {
              onApply(value.start ?? undefined, value.end ?? undefined);
              onClose();
            }}
          />
        </Stack>
      </Stack>
    </Modal>
  );
}

export function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h2
      style={{
        fontSize: 22,
        fontWeight: 600,
        lineHeight: '26px',
        margin: 0,
        fontFamily: 'var(--font-display)',
        color: 'var(--color-text-primary)',
      }}
    >
      {children}
    </h2>
  );
}
